import type {
  IUninstallReasonStepContent,
  IUninstallReasonStepSpecifyFurther,
} from '../types/uninstall';
import type { IGStepDot } from '~/presentation/ui';

export const GEMX_UNINSTALL_DATA_KEY = 'gemx:survey:uninstall';

export const UNINSTALL_MODAL_STEPS = {
  UNINSTALL_REASON: 1,
  CONFIRM_UNINSTALLATION: 2,
  REMOVE_TEMPLATE: 3,
  UNINSTALL_GUIDE: 4,
};

export const UNINSTALL_STEPS_DOTS: IGStepDot[] = [
  { step: 1, title: 'Uninstall reason' },
  { step: 2, title: 'Confirm uninstallation' },
  { step: 3, title: 'Remove templates' },
];

const UNINSTALL_KNOW_MORE_TEXT = "We're sad to see you leaving. Let us know more about the reason";

const UNINSTALL_FEED_BACK: Record<
  'specifyFurther' | 'describeIssues' | 'detailFurther' | 'whySwitchToThis',
  IUninstallReasonStepSpecifyFurther
> = {
  specifyFurther: {
    label: 'Can you specify it further?',
  },
  describeIssues: {
    label: 'Can you describe a little more about the issues?',
  },
  detailFurther: {
    label: 'Please detail further',
  },
  whySwitchToThis: {
    label: 'Why do you want to switch to this?',
  },
};

const UNINSTALL_REASON_PRICE_BUDGET: IUninstallReasonStepContent = {
  label: 'Price/Budget',
  value: 'Price/Budget',
  detail: {
    title: 'What is your opinion on our pricing?',
    description: UNINSTALL_KNOW_MORE_TEXT,
    contents: [
      { label: "It's too expensive", value: "It's too expensive" },
      { label: 'I need a free plan', value: 'I need a free plan' },
    ],
  },
};

const UNINSTALL_REASON_HARD_TO_USE: IUninstallReasonStepContent = {
  label: 'Hard to use',
  value: 'Hard to use',
  detail: {
    title: 'What aspects of GemX do you find difficult to use?',
    description: UNINSTALL_KNOW_MORE_TEXT,
    contents: [
      {
        label: 'Interface is hard to navigate',
        value: 'Interface is hard to navigate',
        feedback: UNINSTALL_FEED_BACK.specifyFurther,
      },
      {
        label: 'App is lack of instructions/tutorials',
        value: 'App is lack of instructions/tutorials',
        feedback: UNINSTALL_FEED_BACK.specifyFurther,
      },
    ],
  },
};

const UNINSTALL_REASON_MISSING_FEATURES: IUninstallReasonStepContent = {
  label: 'Missing features',
  value: 'Missing features',
  detail: {
    title: 'What features is GemX missing?',
    description: UNINSTALL_KNOW_MORE_TEXT,
    contents: [
      {
        label: 'Lacks of testing types',
        value: 'Lacks of testing types',
        feedback: UNINSTALL_FEED_BACK.specifyFurther,
      },
      {
        label: 'Lacks of metrics',
        value: 'Lacks of metrics',
        feedback: UNINSTALL_FEED_BACK.specifyFurther,
      },
      {
        label: 'Lacks of advanced settings',
        value: 'Lacks of advanced settings',
        feedback: UNINSTALL_FEED_BACK.specifyFurther,
      },
    ],
  },
};

const UNINSTALL_REASON_TECHNICAL_ISSUES: IUninstallReasonStepContent = {
  label: 'Technical issues',
  value: 'Technical issues',
  detail: {
    title: 'What technical issues are you facing?',
    description: UNINSTALL_KNOW_MORE_TEXT,
    contents: [
      {
        label: 'Setting up an experiment',
        value: 'Setting up an experiment',
        feedback: UNINSTALL_FEED_BACK.describeIssues,
      },
      {
        label: 'Analytics/Insights',
        value: 'Analytics/Insights',
        feedback: UNINSTALL_FEED_BACK.describeIssues,
      },
      {
        label: 'Other',
        value: 'Other',
        feedback: UNINSTALL_FEED_BACK.describeIssues,
      },
    ],
  },
};

const UNINSTALL_REASON_ANALYTIC_ISSUES: IUninstallReasonStepContent = {
  label: 'Analytic issues',
  value: 'Analytic issues',
  detail: {
    title: 'What analytic issues are you experiencing?',
    description: UNINSTALL_KNOW_MORE_TEXT,
    contents: [
      {
        label: "The data isn't tracking correctly",
        value: "The data isn't tracking correctly",
        feedback: UNINSTALL_FEED_BACK.describeIssues,
      },
      {
        label: "There's a significant delay in tracking data",
        value: "There's a significant delay in tracking data",
        feedback: UNINSTALL_FEED_BACK.describeIssues,
      },
      {
        label: 'The data conflicts with other tracking apps',
        value: 'The data conflicts with other tracking apps',
        feedback: UNINSTALL_FEED_BACK.describeIssues,
      },
      {
        label: 'Other',
        value: 'Other',
        feedback: UNINSTALL_FEED_BACK.describeIssues,
      },
    ],
  },
};

const UNINSTALL_REASON_SWITCH_TO_ANOTHER_SOLUTION: IUninstallReasonStepContent = {
  label: 'Switch to another solution',
  value: 'Switch to another solution',
  detail: {
    title: 'Do you have any other solutions in mind?',
    description: UNINSTALL_KNOW_MORE_TEXT,
    contents: [
      {
        label: 'Intelligems',
        value: 'Intelligems',
        feedback: UNINSTALL_FEED_BACK.whySwitchToThis,
      },
      {
        label: 'Shoplift',
        value: 'Shoplift',
        feedback: UNINSTALL_FEED_BACK.whySwitchToThis,
      },
      {
        label: 'Zipify',
        value: 'Zipify',
        feedback: UNINSTALL_FEED_BACK.whySwitchToThis,
      },
      {
        label: 'Other',
        value: 'Other',
        feedback: { label: 'Mind telling us which one?' },
      },
    ],
  },
};

const UNINSTALL_REASON_NOT_NEEDED_ANYMORE: IUninstallReasonStepContent = {
  label: 'Not needed anymore',
  value: 'Not needed anymore',
  detail: {
    title: 'What made you stop using it?',
    description: UNINSTALL_KNOW_MORE_TEXT,
    contents: [
      {
        label: "I don't need to optimize my pages anymore",
        value: "I don't need to optimize my pages anymore",
        feedback: UNINSTALL_FEED_BACK.detailFurther,
      },
      {
        label: "I don't need to track data anymore",
        value: "I don't need to track data anymore",
        feedback: UNINSTALL_FEED_BACK.detailFurther,
      },
      {
        label: "I don't need A/B test anymore",
        value: "I don't need A/B test anymore",
        feedback: UNINSTALL_FEED_BACK.detailFurther,
      },
      { label: 'Other', value: 'Other', feedback: UNINSTALL_FEED_BACK.detailFurther },
    ],
  },
};

export const UNINSTALL_REASON_ANOTHER_REASON: IUninstallReasonStepContent = {
  label: 'Another reason',
  value: 'Another reason',
  feedback: UNINSTALL_FEED_BACK.detailFurther,
};

export const UNINSTALL_REASON_STEP_CONTENTS: IUninstallReasonStepContent[] = [
  UNINSTALL_REASON_PRICE_BUDGET,
  UNINSTALL_REASON_HARD_TO_USE,
  UNINSTALL_REASON_MISSING_FEATURES,
  UNINSTALL_REASON_TECHNICAL_ISSUES,
  UNINSTALL_REASON_ANALYTIC_ISSUES,
  UNINSTALL_REASON_SWITCH_TO_ANOTHER_SOLUTION,
  UNINSTALL_REASON_NOT_NEEDED_ANYMORE,
  UNINSTALL_REASON_ANOTHER_REASON,
];
